/* eslint-disable max-len, padded-blocks */
/* This mixin provides methods for getting form schemas */
import { mapState } from 'vuex';

export default {
  computed: {
    ...mapState(
      {
        tualatinContacts: (state) => state.tualatinContacts,
      },
    ),
  },
  methods: {
    getSchema() {
      const schema = {
        agGroups: [
          this.getPlanBasicsGroup(),
          this.getPropertyDescriptionGroup(),
          this.getIntroduction(),
          this.getPropertyOverviewGroup(),
          this.getResourceAnalysisGroup(),
          this.getManagementRecommendations(),
        ],
        forestGroups: [
          this.getPlanBasicsGroup(),
          this.getPropertyDescriptionGroup(),
          this.getIntroduction(),
          this.getPropertyOverviewGroup(),
          this.getUnitDetails(),
          this.getResourceAnalysisGroup(),
          this.getManagementRecommendations(),
        ],
        practiceSchema: this.getPracticeSchema(),
        unitSchema: this.getUnitSchema(),
      };
      return schema;
    },
    getPlanBasicsGroup() {
      return {
        id: 'planBasics',
        legend: 'Plan Basics',
        headerName: 'Plan Basics',
        fields: [
          {
            type: 'input',
            inputType: 'text',
            label: this.managementPlan.planType.includes('Forest') ? '<span class="label-name">Forest Name</span>' : '<span class="label-name">Farm Name</span>',
            model: 'basic.planName',
            required: true,
            min: 3,
            max: 50,
            styleClasses: 'row',
            validator: 'string',
          },
          {
            type: 'select',
            label: '<span class="label-name">Tualatin SWCD Plan Author</span>',
            model: 'basic.author',
            required: true,
            styleClasses: 'row',
            values: this.tualatinContacts.map((contact) => `${contact.firsT_NAME} ${contact.lasT_NAME}`),
          },
          {
            type: 'customMonthpicker',
            label: '<span class="label-name">Plan Month/Year</span>',
            model: 'basic.planMonthYear',
            required: true,
            styleClasses: 'row',
          },
        ],
      };
    },
    getPropertyDescriptionGroup() {

      const propertyDescriptionGroup = {
        id: 'propertyDescription',
        legend: 'Property Description',
        headerName: 'Property Description',
        fields: [],
      };

      const fields = [
        {
          type: 'input',
          inputType: 'text',
          label: '<span class="label-name">UTM Coordinates</span><br/><span class="help-text"></span>',
          model: 'propertyDescription.coordinatesUTM',
          attributes: {
            wrapper: { 'data-list': 'col-5' },
            label: { 'data-list': 'col-2' },
          },
          styleClasses: 'row',
          forest: true,
        },
        {
          type: 'input',
          inputType: 'text',
          label: '<span class="label-name">Lat/Long Coordinates</span><br/><span class="help-text"></span>',
          model: 'propertyDescription.coordinatesLatLong',
          attributes: {
            wrapper: { 'data-list': 'col-5' },
          },
          styleClasses: 'row',
          forest: true,
        },
        {
          type: 'input',
          inputType: 'text',
          label: '<span class="label-name">Zoning</span><br/><span class="help-text"></span>',
          model: 'propertyDescription.zoning',
          attributes: {
            wrapper: { 'data-list': 'col-5' },
          },
          styleClasses: 'row',
        },
        {
          type: 'textArea',
          label: '<span class="label-name">Fire Districts</span><br/><span class="help-text"></span>',
          model: 'propertyDescription.fireDistricts',
          attributes: {
            wrapper: { 'data-list': 'col-5' },
          },
          styleClasses: 'row',
          forest: true,
        },
        {
          type: 'textArea',
          label: '<span class="label-name">Native Seed Zones</span><br/><span class="help-text"></span>',
          model: 'propertyDescription.nativeSeedZones',
          attributes: {
            wrapper: { 'data-list': 'col-5' },
          },
          styleClasses: 'row',
          forest: true,
        },
      ];

      if (this.managementPlan.planType.includes('Forest')) {
        propertyDescriptionGroup.fields = fields;
      } else {
        propertyDescriptionGroup.fields = fields.filter((field) => field.forest !== true);
      }

      return propertyDescriptionGroup;
    },
    getPropertyDescriptionAddresses() {
      this.siteObjects.forEach((siteObject) => {
        this.managementPlan.propertyDescription.addresses += `${siteObject.Site_Address}, ${siteObject.City_c}, ${siteObject.County}\n`;
      });
    },
    getIntroduction() {
      return {
        id: 'planIntro',
        legend: 'Introduction',
        headerName: 'Introduction',
        fields: [
          {
            type: 'customRichText',
            label: '<span class="label-name">Background</span>',
            model: 'introduction.background',
            attributes: {
              wrapper: { 'data-list': 'col-5' },
            },
            styleClasses: 'row assessment-text',
          },
          {
            type: 'customRichText',
            label: '<span class="label-name">Landowner Goals</span>',
            model: 'introduction.goals',
            attributes: {
              wrapper: { 'data-list': 'col-5' },
            },
            styleClasses: 'row assessment-text',
          },
        ],
      };
    },
    getPropertyOverviewGroup() {
      return {
        id: 'propertyOverview',
        legend: 'Property Overview',
        headerName: 'Property Overview',
        fields: [
          {
            type: 'customRichText',
            label: '<span class="label-name">Topography</span>',
            model: 'propertyOverview.topography',
            attributes: {
              wrapper: { 'data-list': 'col-5' },
            },
            styleClasses: 'row assessment-text',
          },
          {
            type: 'customRichText',
            label: '<span class="label-name">Soils and Geology</span>',
            model: 'propertyOverview.soilsAndGeology',
            attributes: {
              wrapper: { 'data-list': 'col-5' },
            },
            styleClasses: 'row assessment-text',
          },
          {
            type: 'customRichText',
            label: '<span class="label-name">Water Resources</span>',
            model: 'propertyOverview.waterResources',
            attributes: {
              wrapper: { 'data-list': 'col-5' },
            },
            styleClasses: 'row assessment-text',
          },
          {
            type: 'customRichText',
            label: '<span class="label-name">Water Rights</span>',
            model: 'propertyOverview.waterRights',
            visible: (model) => model && model.planType && !model.planType.includes('Forest'),
            attributes: {
              wrapper: { 'data-list': 'col-5' },
            },
            styleClasses: 'row assessment-text',
          },
          {
            type: 'customRichText',
            label: '<span class="label-name">Irrigation</span>',
            model: 'propertyOverview.irrigation',
            visible: (model) => model && model.planType && !model.planType.includes('Forest'),
            attributes: {
              wrapper: { 'data-list': 'col-5' },
            },
            styleClasses: 'row assessment-text',
          },
          {
            type: 'customRichText',
            label: '<span class="label-name">Property Access, Roads, and Infrastructure</span>',
            model: 'propertyOverview.propertyAccess',
            attributes: {
              wrapper: { 'data-list': 'col-5' },
            },
            styleClasses: 'row assessment-text',
          },
        ],
      };
    },
    getResourceAnalysisGroup() {
      return {
        id: 'resourceAnalysis',
        legend: 'Resource Analysis',
        headerName: 'Resource Analysis',
        fields: [
          {
            type: 'customRichText',
            label: '<span class="label-name">Land Uses Description</span>',
            model: 'resourceAnalysis.landUseDescription',
            attributes: {
              wrapper: { 'data-list': 'col-5' },
            },
            styleClasses: 'row assessment-text',
          },
          {
            type: 'customRichText',
            label: '<span class="label-name">Forest Vegetation</span>',
            model: 'resourceAnalysis.forestVegetation',
            visible: (model) => model && model.planType && model.planType.includes('Forest'),
            attributes: {
              wrapper: { 'data-list': 'col-5' },
            },
            styleClasses: 'row assessment-text',
          },
          {
            type: 'customRichText',
            label: '<span class="label-name">Crops</span>',
            model: 'resourceAnalysis.crops',
            visible: (model) => model && model.planType && !model.planType.includes('Forest'),
            attributes: {
              wrapper: { 'data-list': 'col-5' },
            },
            styleClasses: 'row assessment-text',
          },
          {
            type: 'customRichText',
            label: '<span class="label-name">Pasture and Livestock</span>',
            model: 'resourceAnalysis.pastureAndLivestock',
            visible: (model) => model && model.planType && !model.planType.includes('Forest'),
            attributes: {
              wrapper: { 'data-list': 'col-5' },
            },
            styleClasses: 'row assessment-text',
          },
          {
            type: 'customRichText',
            label: '<span class="label-name">Associated Agricultural Lands</span>',
            model: 'resourceAnalysis.associatedAgriculturalLands',
            visible: (model) => model && model.planType && !model.planType.includes('Forest'),
            attributes: {
              wrapper: { 'data-list': 'col-5' },
            },
            styleClasses: 'row assessment-text',
          },
          {
            type: 'customRichText',
            label: '<span class="label-name">Fish and Wildlife</span>',
            model: 'resourceAnalysis.fishAndWildlife',
            attributes: {
              wrapper: { 'data-list': 'col-5' },
            },
            styleClasses: 'row assessment-text',
          },
          {
            type: 'customRichText',
            label: '<span class="label-name">Integrated Pest Management</span>',
            model: 'resourceAnalysis.integratedPestManagement',
            attributes: {
              wrapper: { 'data-list': 'col-5' },
            },
            styleClasses: 'row assessment-text',
          },
          {
            type: 'customRichText',
            label: '<span class="label-name">Wildfire Preparedness</span>',
            model: 'resourceAnalysis.wildfirePreparedness',
            visible: (model) => model && model.planType && model.planType.includes('Forest'),
            attributes: {
              wrapper: { 'data-list': 'col-5' },
            },
            styleClasses: 'row assessment-text',
          },
          {
            type: 'customRichText',
            label: '<span class="label-name">Cultural Resources</span>',
            model: 'resourceAnalysis.culturalResources',
            attributes: {
              wrapper: { 'data-list': 'col-5' },
            },
            styleClasses: 'row assessment-text',
          },
          {
            type: 'customRichText',
            label: '<span class="label-name">Aesthetics and Recreation</span>',
            model: 'resourceAnalysis.aestheticsAndRecreation',
            visible: (model) => model && model.planType && model.planType.includes('Forest'),
            attributes: {
              wrapper: { 'data-list': 'col-5' },
            },
            styleClasses: 'row assessment-text',
          },
          {
            type: 'customRichText',
            label: '<span class="label-name">Marketing Ecosystem Services</span>',
            model: 'resourceAnalysis.marketingEcosystemServices',
            visible: (model) => model && model.planType && model.planType.includes('Forest'),
            attributes: {
              wrapper: { 'data-list': 'col-5' },
            },
            styleClasses: 'row assessment-text',
          },
          {
            type: 'customRichText',
            label: '<span class="label-name">Regulatory Compliance</span>',
            model: 'resourceAnalysis.regulatoryCompliance',
            attributes: {
              wrapper: { 'data-list': 'col-5' },
            },
            styleClasses: 'row assessment-text',
          },
          {
            type: 'customRichText',
            label: '<span class="label-name">Nutrient and Soil Management</span>',
            model: 'resourceAnalysis.nutrientAndSoilManagement',
            visible: (model) => model && model.planType && !model.planType.includes('Forest'),
            attributes: {
              wrapper: { 'data-list': 'col-5' },
            },
            styleClasses: 'row assessment-text',
          },
        ],
      };
    },
    getManagementRecommendations() {
      return {
        id: 'managementRecommendations',
        legend: 'Management Recommendations',
        headerName: 'Management Recommendations',
        fields: [],
      };
    },
    getUnitDetails() {
      return {
        id: 'unitDetails',
        legend: 'Unit Details',
        headerName: 'Unit Details',
        fields: [],
      };
    },
    getPracticeSchema() {
      return {
        id: 'practiceSchema',
        legend: 'Practice',
        headerName: 'Practice',
        fields: [
          {
            type: 'select',
            label: '<span class="label-name">Practice</span>',
            model: 'practiceTypeId',
            required: true,
            styleClasses: 'row',
            values: this.practiceTypes,
          },
          {
            type: 'radios',
            label: '<span class="label-name">Target Season</span>',
            model: 'targetSeasonId',
            listbox: true,
            styleClasses: 'row',
            values: this.targetSeasons.sort((a, b) => a.sortOrder - b.sortOrder).map((ts) => ({ name: ts.name, value: ts.id })),
          },
          {
            type: 'customYearpicker',
            label: '<span class="label-name">Target Year</span>',
            model: 'targetYear',
            required: true,
            styleClasses: 'row',
          },
          {
            type: 'input',
            inputType: 'text',
            label: '<span class="label-name">Unit(s)/Fields(s)</span>',
            placeholder: 'Enter Unit(s) or Field(s) this Practice applies to',
            model: 'unitsFields',
            required: true,
            validator: 'required',
            attributes: {
              wrapper: { 'data-list': 'col-5' },
            },
            styleClasses: 'row',
          },
          {
            type: 'input',
            inputType: 'number',
            label: '<span class="label-name">Acres</span>',
            model: 'acres',
            required: true,
            validator: (value) => {
              if (!value) return ['This field is required'];
              const regex = /^(\d+)?([.]?\d{0,2})?$/;
              if (!regex.test(value)) {
                return ['Numeric field, up to 2 decimal, greater than 0'];
              }
              return [];
            },
            attributes: {
              wrapper: { 'data-list': 'col-5' },
            },
            styleClasses: 'row',
          },
          {
            type: 'textArea',
            label: '<span class="label-name">Notes</span><br/><span class="help-text">(Max 50 characters)</span>',
            placeholder: 'Enter Notes here',
            max: 50,
            model: 'notes',
            attributes: {
              wrapper: { 'data-list': 'col-5' },
            },
            styleClasses: 'row',
          },
        ],
      };
    },
    getUnitSchema() {
      return {
        id: 'unitSchema',
        legend: 'Unit Details',
        headerName: 'Unit Details',
        fields: [
          {
            type: 'customRichText',
            label: '<span class="label-name">Details</span><br/><span class="help-text">Fill in descriptive details for a management unit.</span>',
            model: 'details',
            styleClasses: 'row assessment-text',
            buttons: [
              {
                classes: 'btn btn-danger rounded-pill form-delete-button',
                label: 'Delete',
                onclick: (model) => {
                  this.$bvModal.show(`deleteUnitModal-${model.id || model.tempId}`);
                },
              },
            ],
          },
        ],
      };
    },
  },
};
